/**
 * Builds a canonical, query/hash-free base for Schema.org `@id` values from a
 * page URL: `origin + pathname` with a trailing slash. Callers append a
 * fragment (e.g. `#courses`, `#bundle`).
 *
 * Why: callers pass `document.URL`, which carries the full query string and
 * hash (e.g. `?coupon=X`). Naive trailing-slash handling on that raw value
 * produces invalid ids like `https://site/page?coupon=X/` and dedupe keys that
 * change with every coupon/UTM param. Parsing with `new URL()` and dropping
 * `search`/`hash` yields a stable, canonical base.
 *
 * Falls back to naive trailing-slash handling for non-absolute inputs so a
 * malformed URL can never throw mid-SSR.
 *
 * e.g. "https://site/page?coupon=X" -> "https://site/page/"
 */
export function canonicalIdBase(pageUrl: string): string {
  try {
    const { origin, pathname } = new URL(pageUrl);
    const path = pathname.endsWith('/') ? pathname : `${pathname}/`;
    return `${origin}${path}`;
  } catch {
    return pageUrl.endsWith('/') ? pageUrl : `${pageUrl}/`;
  }
}

/**
 * Extracts the origin (scheme + host) from a page URL, for absolutizing relative
 * links. SSR-safe: callers pass `document.URL` rather than reading
 * `document.location.origin`, whose `location` object may be unpopulated on the
 * server or in tests (and would throw). Falls back to an empty string for
 * non-absolute inputs so relative links stay relative instead of breaking
 * rendering mid-SSR.
 *
 * e.g. "https://site/page?coupon=X" -> "https://site"
 */
export function originFromUrl(pageUrl: string): string {
  try {
    return new URL(pageUrl).origin;
  } catch {
    return '';
  }
}

/**
 * Recursively rewrites every absolute http(s) URL string inside a schema value
 * whose host equals `fromHost`, replacing its origin with the canonical brand
 * origin `https://{toHost}` (path/query/hash preserved).
 *
 * Why: component schema builders derive their `@id`/`url` from `document.URL`.
 * On SSR that is the internal ingress request host (e.g.
 * `frontend.ecom-prod.aceable.com`) rather than the public brand domain, and its
 * protocol is `http` (TLS is terminated at the edge). Both would leak into
 * production JSON-LD, so ids come out as `http://frontend.ecom-prod...` instead
 * of the canonical `https://www.aceable.com`. `ServerSchemaService` calls this at
 * serialization time to normalize the request origin to the canonical brand
 * origin — a single chokepoint that also covers future components. The protocol
 * is forced to `https` so component `@id`s match the always-`https` Organization/
 * WebSite ids and never mix schemes. See EPS-4230 (the Organization `@id` was
 * already hardened in EPS-3951).
 *
 * Only URLs whose host matches `fromHost` are touched, so genuinely external
 * URLs (Trustpilot, Allstate, publisher logos) pass through unchanged. Non-URL
 * strings, relative URLs, and malformed values are returned as-is — this never
 * throws mid-SSR. Objects/arrays are cloned; primitives are returned by value.
 */
export function rewriteUrlHost<T>(value: T, fromHost: string, toHost: string): T {
  return rewriteValue(value, fromHost, toHost) as T;
}

/** Recursion helper typed over `unknown` so no `any` propagates through the tree. */
function rewriteValue(value: unknown, fromHost: string, toHost: string): unknown {
  if (typeof value === 'string') {
    return swapUrlOrigin(value, fromHost, toHost);
  }
  if (Array.isArray(value)) {
    return value.map((item) => rewriteValue(item, fromHost, toHost));
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value)) {
      out[key] = rewriteValue(val, fromHost, toHost);
    }
    return out;
  }
  return value;
}

function swapUrlOrigin(str: string, fromHost: string, toHost: string): string {
  if (!/^https?:\/\//i.test(str)) return str;
  try {
    const url = new URL(str);
    if (url.host !== fromHost) return str;
    // Force the canonical https brand origin: component ids must match the
    // always-https Organization/WebSite ids rather than inherit the internal
    // http request scheme.
    url.protocol = 'https:';
    url.host = toHost;
    // The `host` setter leaves the previous port intact when `toHost` omits one,
    // so clear it explicitly to avoid leaking a stale port (e.g. dev `:4000`).
    if (!toHost.includes(':')) url.port = '';
    return url.toString();
  } catch {
    return str;
  }
}
